import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { getBottomPadding } from '../../../utils/sessionStorage'
import { EliteAboutVideoFab } from '../../../components/elite/EliteAboutVideoFab'
import { EliteBuyCtaExtrasProvider } from '../../../components/elite/EliteBuyCtaExtrasContext'
import { useIsClearOfBottomFabZone } from '../../../components/elite/useIsClearOfBottomFabZone'
import {
  ElitePlansFloatingCtaProvider,
  type ElitePlansFloatingCtaContextValue
} from '../ElitePlansFloatingCtaContext'
import { useElitePlansLanding } from '../ElitePlansLandingContext'
import { ELITE_PLANS_ELITE_PRIME_TAB_ID } from '../constants/planIds'
import { ElitePlansPageShell } from './ElitePlansPageShell'
import { ElitePrimeAssistantFab } from './ElitePrimeAssistantFab'

/** Match NewEliteLandingPlanSelector — floating CTA once <20% of the inline CTA remains in view. */
const INLINE_CTA_VISIBILITY_THRESHOLD = 0.2
const MAX_INLINE_CTA_ATTACH_FRAMES = 60

/**
 * Sell-landing shell: sticky buy CTA chrome, About Elite / Prime Assistant FABs, buy-CTA extras.
 *
 * Belongs here: scroll root + FAB/floating-CTA refs for non-family sell pages.
 * Does not belong here: family purchase footer (see `/elite-plans-family-landing`).
 */
export function ElitePlansSellLandingChrome({ children }: { children: ReactNode }) {
  const { status, isStatusLoading, activeTab } = useElitePlansLanding()
  const isElitePrimeTab = activeTab.id === ELITE_PLANS_ELITE_PRIME_TAB_ID
  const scrollRootRef = useRef<HTMLDivElement | null>(null)
  const floatingCtaRef = useRef<HTMLDivElement | null>(null)
  const inlineCtaRef = useRef<HTMLDivElement | null>(null)
  // Start visible so the floating bar stays hidden until the observer attaches
  const [isInlineCtaVisible, setIsInlineCtaVisible] = useState(true)

  useEffect(() => {
    if (isStatusLoading) {
      setIsInlineCtaVisible(true)
      return
    }

    let cancelled = false
    let observer: IntersectionObserver | undefined
    let attachedScrollRoot: HTMLElement | undefined
    let rafId = 0
    let framesTried = 0

    /** Floating only after the inline CTA has scrolled past — not while it is still below the fold. */
    const syncInlineCtaVisibility = (entry?: IntersectionObserverEntry) => {
      if (cancelled) return
      const inlineCtaNode = inlineCtaRef.current
      const scrollRoot = scrollRootRef.current
      if (!inlineCtaNode?.isConnected || !scrollRoot?.isConnected) {
        setIsInlineCtaVisible(true)
        return
      }

      const ctaRect = entry?.boundingClientRect ?? inlineCtaNode.getBoundingClientRect()
      const rootRect = entry?.rootBounds ?? scrollRoot.getBoundingClientRect()
      const rootTop = rootRect?.top ?? 0
      const rootBottom = rootRect?.bottom ?? 0
      const ctaTop = ctaRect.top
      const ctaBottom = ctaRect.bottom
      const ctaHeight = ctaRect.height

      // Still entirely below the scrollport — keep floating hidden
      if (ctaTop >= rootBottom) {
        setIsInlineCtaVisible(true)
        return
      }
      // Fully above the scrollport — floating on
      if (ctaBottom <= rootTop) {
        setIsInlineCtaVisible(false)
        return
      }
      if (!(ctaHeight > 0)) {
        setIsInlineCtaVisible(true)
        return
      }
      // Match PlanSelector intent: floating once <20% of the inline CTA remains in view
      const visiblePx = Math.min(ctaBottom, rootBottom) - Math.max(ctaTop, rootTop)
      setIsInlineCtaVisible(visiblePx / ctaHeight >= INLINE_CTA_VISIBILITY_THRESHOLD)
    }

    const onScrollRootScroll = () => {
      syncInlineCtaVisibility()
    }

    const attach = () => {
      if (cancelled) return
      const inlineCtaNode = inlineCtaRef.current
      const scrollRoot = scrollRootRef.current
      if (!inlineCtaNode?.isConnected || !scrollRoot?.isConnected) {
        framesTried += 1
        if (framesTried < MAX_INLINE_CTA_ATTACH_FRAMES) {
          rafId = requestAnimationFrame(attach)
        }
        return
      }

      attachedScrollRoot = scrollRoot
      // Scroll listener catches IO gaps when the CTA jumps from below-fold to above-fold
      scrollRoot.addEventListener('scroll', onScrollRootScroll, { passive: true })

      // Observe the inline primary CTA against the page scroll root.
      // Include 0 so enter/leave still notifies when ratio never reaches 0.2.
      observer = new IntersectionObserver(
        (entries) => {
          syncInlineCtaVisibility(entries?.[0])
        },
        { threshold: [0, INLINE_CTA_VISIBILITY_THRESHOLD], root: scrollRoot }
      )
      observer.observe(inlineCtaNode)
      syncInlineCtaVisibility()
    }

    attach()

    return () => {
      cancelled = true
      cancelAnimationFrame(rafId)
      observer?.disconnect()
      attachedScrollRoot?.removeEventListener('scroll', onScrollRootScroll)
    }
  }, [isStatusLoading, activeTab.id])

  const isFloatingCtaVisible = !isInlineCtaVisible
  // About Elite FAB is Elite-only; Prime Assistant FAB is Prime-only
  const showAboutVideoFab = useIsClearOfBottomFabZone(
    inlineCtaRef,
    scrollRootRef,
    !isStatusLoading && !isElitePrimeTab,
    activeTab.id
  )
  const showPrimeAssistantFab = useIsClearOfBottomFabZone(
    inlineCtaRef,
    scrollRootRef,
    !isStatusLoading && isElitePrimeTab,
    activeTab.id
  )

  const floatingCtaValue = useMemo(
    (): ElitePlansFloatingCtaContextValue => ({
      enableFloatingCta: true,
      isFloatingCtaVisible,
      floatingCtaRef,
      inlineCtaRef
    }),
    [isFloatingCtaVisible]
  )

  return (
    <EliteBuyCtaExtrasProvider status={status}>
      <ElitePlansFloatingCtaProvider value={floatingCtaValue}>
        {/* Bottom inset lives on the last sell-body section — avoid stacking empty scroll pad here. */}
        <ElitePlansPageShell scrollRootRef={scrollRootRef} bottomInsetPx={0}>
          {children}
        </ElitePlansPageShell>
        {/* Portal so overflow / tabpanel transform cannot clip the fixed FAB */}
        {isElitePrimeTab
          ? createPortal(
              <ElitePrimeAssistantFab
                // Same gate as About Elite — wait for sticky buy so the FAB clears the hero CTA.
                showFab={showPrimeAssistantFab && isFloatingCtaVisible}
                isEliteCtaVisible={isFloatingCtaVisible}
                eliteCtaRef={floatingCtaRef}
                eliteCtaBottomPx={getBottomPadding() + 16}
              />,
              document.body
            )
          : createPortal(
              <EliteAboutVideoFab
                // Also require floating CTA — tall heroes leave the inline CTA clear of the FAB
                // zone while still below the fold; do not show About Elite until sticky buy shows.
                showFab={showAboutVideoFab && isFloatingCtaVisible}
                isEliteCtaVisible={isFloatingCtaVisible}
                eliteCtaRef={floatingCtaRef}
                eliteCtaBottomPx={getBottomPadding() + 16}
              />,
              document.body
            )}
      </ElitePlansFloatingCtaProvider>
    </EliteBuyCtaExtrasProvider>
  )
}
